// tslint:disable: max-line-length

import { Injectable, Inject } from '@angular/core';
import { Observable, of, combineLatest, forkJoin, throwError } from 'rxjs';
import { DwMdFormActionHandler, DwMdFormActionState, DwMdFormActionResult, DwFormAction, DwSecuritySSOService, DwSecurityRepositoryServiceToken, DwSecurityRepositoryService, DwSecurityUserService, DwFormSaveResult, DwLoggingService, DwLogSeverity, DwFormActionConfig, DwAction, DwFormActionConfigDefaultComponent, DwActionType } from '@devwareapps/devware-cap';
import { map, flatMap, tap, catchError, timeout, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

import { AppMetaDataItemNames, RequestEntity } from 'src/app/meta-data/app-meta-data.service';
import { ConfigService } from 'src/environments/environment';
import { DwMdPdfResult } from '@devwareapps/devware-cap/devware-ui-metadata/form/model/actions/dw-md-pdf-result.model';
import { EquipRequestService } from '../services/equip-request.service';
import { EquipGridReadonlySectionComponent } from '../components/equip-grid-readonly-section/equip-grid-readonly-section.component';
import { Router } from '@angular/router';

@DwAction<DwFormActionConfig> ({
  isGlobal: false,
  actionType: DwActionType.formAction,
  display: 'Send Request E-mail',
  keyName : 'request-email',
  actionMessage: '',
  parentItemName : AppMetaDataItemNames.Request,
  configComponent: DwFormActionConfigDefaultComponent,
  defaultButtonConfig : {
    buttonText : 'Send Request E-mail',
    buttonTitle : 'Send an Equipment Request e-mail',
    command : 'request-email',
    iconClass : 'fa fa-envelope',
    buttonClass : 'btn btn-primary'
  },
  configData : {
    redirectAfter: true,
    actionMessage: 'Sending E-mail...'
  }
})
@Injectable({
  providedIn: 'root'
})
export class EquipEmailActionService implements DwMdFormActionHandler {
  saveResult: DwFormSaveResult;

  itemTimeout = 30000;
  pdfProcessTimeout = 70000;

  constructor(
    private ssoService: DwSecuritySSOService,
    private securityService: DwSecurityUserService,
    @Inject(DwSecurityRepositoryServiceToken) private securityRepository: DwSecurityRepositoryService,
    private http: HttpClient,
    private equipRequestService: EquipRequestService,
    private loggingService: DwLoggingService,
    private router: Router
  ) {
  }

  handleAction(formActionState: DwMdFormActionState): Observable<DwMdFormActionResult> {
    const sucessResult: DwMdFormActionResult = {
      success: true,
      message: 'E-mail sent successfully!',
    };
    const saveFailedresult: DwMdFormActionResult = {
      success: false,
      message: 'Saved Failed!',
    };
    const timeoutFailedresult: DwMdFormActionResult = {
      success: false,
      message: 'The e-mail sending has failed due to a timeout.  Please try again!',
    };

    const JobNumber: string = formActionState.formApi.formGroup.get('JobNumber').value;
    if (formActionState.formApi.formGroup.get('IsAutoPickup').value && JobNumber.search('-A') === -1) {
      formActionState.formApi.formGroup.get('JobNumber').setValue(formActionState.formApi.formGroup.get('JobNumber').value + '-A');
    }

    const request: RequestEntity = formActionState.formApi.formGroup.value;

    // Convert the form to PDF
    const obConvertPdf = this.logObservableState(formActionState.formApi.convertHtmlToPdf(), 'Convert PDF', this.itemTimeout);

    // Get the access token (this will refresh if needed)
    const obAccessToken = this.logObservableState(this.ssoService.getAccessToken(), 'Get Access Token', this.itemTimeout);

    // Changed to not validate since this should occur before the action is fired

    const obSaveData = this.logObservableState(formActionState.formApi.saveData(false), 'Save Data', this.itemTimeout);

    const combinedResult = this.logObservableState(combineLatest(obConvertPdf, obAccessToken, obSaveData), 'Combined Result', this.pdfProcessTimeout);

    return combinedResult.pipe(flatMap(([exportResult, accessToken, saveResult]) => {
        if (!saveResult.success) {
          return of(saveFailedresult);
        }

        // Remove since this is no longer possible
        // if (saveResult.validationFailed) {
        //   return failedresult;
        // }
        var currentEquipRequest = formActionState.formApi.getFormData();

        return this.sendEmail(currentEquipRequest, accessToken, exportResult)
           .pipe(map(emailResult => {
            this.redirectToView(currentEquipRequest);
             return sucessResult;
           }));
      }),
        catchError(error => {
          this.loggingService.logToConsole(`Error sending e-mail`, DwLogSeverity.verbose, error);
          return of(timeoutFailedresult);
        })
      );
  }

  redirectToView(currentEquipRequest) {
    const url = `/project-tools/equipment-requests/view/${currentEquipRequest.RequestId}`;
    this.router.navigateByUrl(url);
  }

  logObservableState(ob: Observable<any>, obName: string, timeoutValue: number = -1): Observable<any> {
    if (timeoutValue === -1) {
      timeoutValue = this.pdfProcessTimeout;
    }
    this.loggingService.logToConsole(`Watching Observable ${obName}`, DwLogSeverity.verbose);

    return ob.pipe(
      timeout(timeoutValue || 1000000000), // Add a timeout to all observables (This will work)
      take(1), // Only take the first result - this prevents a timeout error displaying when the e-mail sending is complete
      tap((result) => {
        this.loggingService.logToConsole(`Observable ${obName} completed`, result, DwLogSeverity.verbose);
      }),
    catchError(error => {
      this.loggingService.logToConsole(`Observable ${obName} errored`, error, DwLogSeverity.error);
      const timeoutFailedresult: DwMdFormActionResult = {
        success: false,
        message: 'The e-mail sending has failed due to a timeout.  Please try again!',
      };

      return of(timeoutFailedresult);
      // return throwError(`${obName} failed with error`);
    }));
  }

  sendEmail(request: RequestEntity, accessToken: string, pdfResult?: DwMdPdfResult): Observable<any> {
    try{
    const url = 'https://graph.microsoft.com/v1.0/me/sendMail';
    this.loggingService.logToConsole('Start building Email', null, DwLogSeverity.verbose);
    const email = this.buildEmail(request, accessToken, pdfResult);
    this.loggingService.logToConsole('Build Email Complete', null, DwLogSeverity.verbose);
    const resultOb = this.http.post(url, email, { headers: { Authorization: `Bearer ${accessToken}`, omitCapHeaders: 'true' } });

    return resultOb;
    }
    catch(error) {
      this.loggingService.logToConsole('Error sending Email', error, DwLogSeverity.error);
    }
  }

  buildEmail (request: RequestEntity, accessToken: string, pdfResult?: DwMdPdfResult){
    let toEmail = request.CompanyEmail;
    let ccEmail : string = request.EmailCopyString;
    //console.log(request.DeliveryOptionCv);
    // Configuration setting to send this to the current user (for testing purposes)

    if (ConfigService.config.emailSendToCurrentUserOnly) {
      toEmail = 'wAppsTesting@wachter.com';
      ccEmail = 'wAppsTesting@wachter.com';
    }
    let ccEmailArray = ccEmail == null ? [] :ccEmail.split(/,|;/) ;
    const toEmailArray = toEmail.split(/,|;/);

    let companyId = request.CompanyId;
    let prefix : string = companyId == 11 ? '*** Target *** ' : '';
    const email = {
      message: {
        subject: prefix + 'Equipment Needed for ' + (request.DeliveryOptionCv == 130 ? 'Delivery on ' : (request.DeliveryOptionCv == 132 ? 'Quote on ' : 'Pickup on ')) + new Date(request.NeededByDateTime).toDateString() + ', Ref:' + request.JobNumber,
        body: {
          contentType: 'HTML',
          content: this.equipRequestService.generateHtml(request)
        },
        toRecipients: [],
        ccRecipients: [],
        bccRecipients: [{
          emailAddress: {
            address: 'osg@wachter.com'
          }
        }],
        importance: 'High',
        hasAttachments: true,
        attachments: [
          {
            '@odata.type': '#microsoft.graph.fileAttachment',
            name: 'Equipment Needed for ' + (request.DeliveryOptionCv == 130 ? 'Delivery on ' : (request.DeliveryOptionCv == 132 ? 'Quote on ' : 'Pickup on ')) + new Date(request.NeededByDateTime).toDateString() + ', Ref:' + request.JobNumber + '.pdf',
            contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            contentBytes: pdfResult.Base64Data,
          }
        ]
      }
    };
    if (request.EmailCopyString !== '' && request.EmailCopyString != null) {
      ccEmailArray.forEach(x => {
        if (x !== '') {
          email.message.ccRecipients.push({
            emailAddress: {
              address: x.trim()
            }
          });
        }

      });
    }
    if (request.CompanyEmail !== '' && request.CompanyEmail != null) {
      toEmailArray.forEach(x => {
        if (x !== '') {
          email.message.toRecipients.push({
            emailAddress: {
              address: x.trim()
            }
          });
        }

      });
    }
    return email;
  }
}
